// ─────────────────────────────────────────────────────────────
//  SolarSavings — API 工具层
//  NREL PVWatts · OpenEI 电价 · Google Geocoding
//  （Google Solar API 已移除 — 按次收费）
// ─────────────────────────────────────────────────────────────

const GOOGLE_KEY  = 'AIzaSyBlnjwQLprJe-x9-CnSa3Y8N4lp6o7mU6w';
const NREL_KEY    = 'ags7tZlyqh9xj2EvfEhmddpjbu4ncdmeg6VhjK22';
const PVWATTS_URL = 'https://developer.nrel.gov/api/pvwatts/v8.json';

// ── 1. Google Geocoding — 地址转经纬度 ───────────────────────
export async function geocodeAddress(address) {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Geocoding failed');
  const data = await res.json();
  if (!data.results?.length) throw new Error('Address not found');
  const loc = data.results[0].geometry.location;
  return { lat: loc.lat, lng: loc.lng, formattedAddress: data.results[0].formatted_address };
}

// ── 2. NREL PVWatts — 逐月发电量预测 ─────────────────────────
export async function fetchPVWatts(lat, lng, systemKW, tilt = 20, azimuth = 180) {
  const params = new URLSearchParams({
    api_key: NREL_KEY,
    lat: lat.toFixed(5),
    lon: lng.toFixed(5),
    system_capacity: Math.max(systemKW, 0.5).toFixed(2),
    azimuth,
    tilt,
    array_type: 1,
    module_type: 1,
    losses: 14,
  });
  const res = await fetch(`${PVWATTS_URL}?${params}`);
  if (!res.ok) throw new Error(`PVWatts error: ${res.status}`);
  const data = await res.json();
  if (data.errors?.length) throw new Error(data.errors.join(', '));

  const monthly = data.outputs?.ac_monthly || [];
  const annualKWh = data.outputs?.ac_annual || 0;
  const capacityFactor = data.outputs?.capacity_factor || 0;
  const solradAnnual = data.outputs?.solrad_annual || 0;
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return {
    annualKWh:      Math.round(annualKWh),
    capacityFactor: parseFloat(capacityFactor.toFixed(1)),
    solradAnnual:   parseFloat(solradAnnual.toFixed(2)),
    monthlyData:    monthly.map((kwh, i) => ({ month: MONTHS[i], kwh: Math.round(kwh) })),
  };
}

// ── 3. CA Utility Rate Lookup — 按zip前缀匹配真实电价 ─────────
// 数据来源：CPUC 2024年各电力公司平均居民电价（含所有附加费）
// PG&E E-1: ~$0.35, SCE D: ~$0.32, SDG&E DR: ~$0.44, SMUD: ~$0.13, LADWP: ~$0.22
const CA_UTILITY_RATES = [
  // PG&E — 北加州 + 中央谷地
  { prefixes: ['94','95','93','92720'], name: 'PG&E', rate: 0.35 },
  // SCE — 南加州（LA周边、内陆帝国、橙县部分）
  { prefixes: ['900','901','902','903','904','905','906','907','908','909','910','911','912','913','914','915','916','917','918','919','920','921','922','923','924','925','926','927','928'], name: 'SCE', rate: 0.32 },
  // SDG&E — 圣地亚哥县
  { prefixes: ['919','920','921','922'], name: 'SDG&E', rate: 0.44 },
  // SMUD — Sacramento市区
  { prefixes: ['9581','9582','9583','9584','9585','9586','9587','9588'], name: 'SMUD', rate: 0.13 },
  // LADWP — LA市内
  { prefixes: ['900','901','902','903','904','905','906','907','908'], name: 'LADWP', rate: 0.22 },
];

// zip → utility 精确映射（优先级高于前缀）
const ZIP_UTILITY_MAP = {
  // SMUD zips (Sacramento city)
  '95811':'SMUD','95814':'SMUD','95815':'SMUD','95816':'SMUD','95817':'SMUD',
  '95818':'SMUD','95819':'SMUD','95820':'SMUD','95821':'SMUD','95822':'SMUD',
  '95823':'SMUD','95824':'SMUD','95825':'SMUD','95826':'SMUD','95827':'SMUD',
  '95828':'SMUD','95829':'SMUD','95830':'SMUD','95831':'SMUD','95832':'SMUD',
  '95833':'SMUD','95834':'SMUD','95835':'SMUD','95838':'SMUD','95841':'SMUD',
  // LADWP zips
  '90001':'LADWP','90002':'LADWP','90003':'LADWP','90004':'LADWP','90005':'LADWP',
  '90006':'LADWP','90007':'LADWP','90008':'LADWP','90010':'LADWP','90011':'LADWP',
  '90012':'LADWP','90013':'LADWP','90014':'LADWP','90015':'LADWP','90016':'LADWP',
  '90017':'LADWP','90018':'LADWP','90019':'LADWP','90020':'LADWP','90021':'LADWP',
  '90023':'LADWP','90024':'LADWP','90025':'LADWP','90026':'LADWP','90027':'LADWP',
  '90028':'LADWP','90029':'LADWP','90031':'LADWP','90032':'LADWP','90033':'LADWP',
  '90034':'LADWP','90035':'LADWP','90036':'LADWP','90037':'LADWP','90038':'LADWP',
  '90039':'LADWP','90041':'LADWP','90042':'LADWP','90043':'LADWP','90044':'LADWP',
  '90045':'LADWP','90046':'LADWP','90047':'LADWP','90048':'LADWP','90049':'LADWP',
  // SDG&E zips
  '91901':'SDG&E','91902':'SDG&E','91910':'SDG&E','91911':'SDG&E','91914':'SDG&E',
  '91915':'SDG&E','91932':'SDG&E','91941':'SDG&E','91942':'SDG&E','91945':'SDG&E',
  '91950':'SDG&E','92007':'SDG&E','92008':'SDG&E','92009':'SDG&E','92010':'SDG&E',
  '92011':'SDG&E','92014':'SDG&E','92020':'SDG&E','92021':'SDG&E','92024':'SDG&E',
  '92025':'SDG&E','92026':'SDG&E','92027':'SDG&E','92028':'SDG&E','92037':'SDG&E',
  '92040':'SDG&E','92054':'SDG&E','92056':'SDG&E','92057':'SDG&E','92064':'SDG&E',
  '92065':'SDG&E','92067':'SDG&E','92069':'SDG&E','92071':'SDG&E','92075':'SDG&E',
  '92078':'SDG&E','92082':'SDG&E','92083':'SDG&E','92084':'SDG&E','92085':'SDG&E',
  '92091':'SDG&E','92096':'SDG&E','92101':'SDG&E','92102':'SDG&E','92103':'SDG&E',
  '92104':'SDG&E','92105':'SDG&E','92106':'SDG&E','92107':'SDG&E','92108':'SDG&E',
  '92109':'SDG&E','92110':'SDG&E','92111':'SDG&E','92113':'SDG&E','92114':'SDG&E',
  '92115':'SDG&E','92116':'SDG&E','92117':'SDG&E','92119':'SDG&E','92120':'SDG&E',
  '92121':'SDG&E','92122':'SDG&E','92123':'SDG&E','92124':'SDG&E','92126':'SDG&E',
  '92127':'SDG&E','92128':'SDG&E','92129':'SDG&E','92130':'SDG&E','92131':'SDG&E',
  '92132':'SDG&E','92134':'SDG&E','92139':'SDG&E','92140':'SDG&E','92145':'SDG&E',
  '92154':'SDG&E','92173':'SDG&E',
};

const UTILITY_INFO = {
  'PG&E':  { rate: 0.35, note: 'E-1 Tiered Rate 2024' },
  'SCE':   { rate: 0.32, note: 'Domestic TOU-D-4-9PM 2024' },
  'SDG&E': { rate: 0.44, note: 'DR-SES Rate 2024' },
  'SMUD':  { rate: 0.13, note: 'Residential Time-of-Day 2024' },
  'LADWP': { rate: 0.22, note: 'Residential Schedule D 2024' },
};

export function getUtilityRateByZip(zip) {
  if (!zip || zip.length < 5) return { avgRatePerKWh: 0.33, utilityName: 'CA Average', source: 'CA avg' };
  // 精确zip匹配
  const exactUtility = ZIP_UTILITY_MAP[zip];
  if (exactUtility && UTILITY_INFO[exactUtility]) {
    const info = UTILITY_INFO[exactUtility];
    return { avgRatePerKWh: info.rate, utilityName: exactUtility, rateName: info.note, source: 'CPUC 2024' };
  }
  // SDG&E: 919xx-922xx
  const p3 = zip.slice(0, 3);
  if (['919','920','921','922'].includes(p3)) {
    return { avgRatePerKWh: 0.44, utilityName: 'SDG&E', rateName: 'DR-SES Rate 2024', source: 'CPUC 2024' };
  }
  // PG&E: 94xxxx, 95xxxx, 93xxxx (non-SMUD)
  const p2 = zip.slice(0, 2);
  if (['94','95','93'].includes(p2)) {
    return { avgRatePerKWh: 0.35, utilityName: 'PG&E', rateName: 'E-1 Tiered 2024', source: 'CPUC 2024' };
  }
  // SCE: 90xxx-91xxx (non-LADWP)
  if (['90','91'].includes(p2)) {
    return { avgRatePerKWh: 0.32, utilityName: 'SCE', rateName: 'TOU-D-4-9PM 2024', source: 'CPUC 2024' };
  }
  return { avgRatePerKWh: 0.33, utilityName: 'CA Average', source: 'CA avg' };
}

export async function fetchElectricityRate(lat, lng, zip) {
  return getUtilityRateByZip(zip);
}

// ── 4. 主入口 ─────────────────────────────────────────────────
export async function fetchAllSolarData(address, systemKW, zip) {
  const { lat, lng, formattedAddress } = await geocodeAddress(address);

  const [pvResult, rateResult] = await Promise.allSettled([
    fetchPVWatts(lat, lng, systemKW || 10),
    fetchElectricityRate(lat, lng, zip),
  ]);

  const pv   = pvResult.status   === 'fulfilled' ? pvResult.value   : null;
  const rate = rateResult.status === 'fulfilled' ? rateResult.value : { avgRatePerKWh: 0.33, utilityName: 'CA Average', source: 'CA avg' };

  return { lat, lng, formattedAddress, pv, rate };
}
